import React from 'react';
import { Box, Divider, Stack, Typography } from '@mui/material';


/**
 * One consistent title/subtitle/actions layout, shared by every section on
 * both Timing and Pulse (originally written for Timing, where every chart
 * and table already used it; Pulse's sections were each hand-rolling the
 * same title+subtitle Stack slightly differently, plus a couple of grids
 * that only had a small uppercase "shelf label" with no subtitle at all).
 * One component now, so "every section looks the same" is enforced by
 * reuse, not by eye.
 *
 * ``subtitle`` is a single line, always visible (no click needed) that
 * says what the section shows AND, since a page with several sections can
 * each be scoped to a different time window, what time period/population
 * it covers -- e.g. "this week vs. the last 4 weeks" or "leads created in
 * the last 7 days." The deeper "how this is calculated" detail still lives
 * behind each section's own ``ExplainerToggle``/``ExplainerNote`` pair,
 * unchanged.
 *
 * A ``Divider`` always closes out the header, baked in here rather than
 * added by each section individually -- so "header, then a clean line,
 * then the actual chart/table/explainer" is one consistent rule enforced
 * by reuse, not a per-page styling choice that can quietly drift.
 *
 * ``actions`` ordering convention (left to right): scope/clock badges,
 * then download (if any), then the info ``ExplainerToggle`` LAST -- every
 * section on these pages ends its top row on the info icon, so a reader
 * always finds it in the same spot. A section-specific STATEFUL control
 * (a toggle/switch that changes what's rendered below, e.g. Plan
 * Roster's "Compare with current schedule") does not belong crammed onto
 * that same row -- it reads as a fifth badge rather than the one thing
 * on this row a reader can actually interact with. Pass it as
 * ``secondaryActions`` instead: its own right-aligned row underneath,
 * still part of the same header block, above the ``Divider``.
 */
export default function ChartSectionHeader({
  title,
  subtitle,
  actions,
  secondaryActions,
}: {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
  secondaryActions?: React.ReactNode;
}) {
  return (
    <Box sx={{ mb: 1.5 }}>
      {/* A grid, not a wrapping flex row: with `flexWrap`, a long subtitle
          that pushes `actions` onto its own line ends up left-aligned
          there instead of staying top-right (a single item on a
          `space-between` flex line has nothing to space itself from).
          Grid columns don't reflow like that -- `actions` stays pinned in
          its own column, top right, no matter how much the title/subtitle
          wrap internally. */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) auto',
          alignItems: 'flex-start',
          columnGap: 2,
          pb: secondaryActions ? 0.75 : 1,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        {actions && (
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
            {actions}
          </Stack>
        )}
      </Box>
      {secondaryActions && (
        <Stack direction="row" justifyContent="flex-end" alignItems="center" sx={{ pb: 1 }}>
          {secondaryActions}
        </Stack>
      )}
      <Divider />
    </Box>
  );
}
